import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PortalSelector } from '../PortalSelector';
import { NotificationCenter } from './NotificationCenter';
import {
  LayoutDashboard,
  UserPlus,
  Plus,
  LayoutGrid,
  Users,
  Beaker,
  Calendar,
  Target,
  FileText,
  FolderOpen,
  Link2,
  Wrench,
  LogOut,
  ChevronRight,
  Menu,
  X,
  Search,
  Home,
  Building2,
  Phone,
  MapPin,
  Loader2,
  Trophy,
  Sun,
  Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Navigation Item Component
function NavItem({ to, icon: Icon, label, badge, active, onClick, highlight, disabled }) {
  const content = (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
        ${disabled 
          ? 'text-zinc-600 cursor-not-allowed opacity-50'
          : highlight 
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30' 
            : active 
              ? 'bg-white/10 text-white' 
              : 'text-zinc-400 hover:text-white hover:bg-white/5'
        }
      `}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${highlight ? 'text-emerald-400' : ''}`} />
      <span className={`text-sm font-medium flex-1 truncate ${highlight ? 'font-semibold' : ''}`}>{label}</span>
      {badge && (
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
          highlight ? 'bg-emerald-500 text-white' : 'bg-white text-black'
        }`}>
          {badge}
        </span>
      )}
      {active && !highlight && <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
    </div>
  );

  if (disabled) {
    return <div className="cursor-not-allowed">{content}</div>;
  }

  return (
    <Link to={to} onClick={onClick}>
      {content}
    </Link>
  );
}

// Section divider
function NavSection({ title }) {
  return (
    <div className="pt-4 pb-2 px-4">
      <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-medium font-['Manrope']">
        {title}
      </span>
    </div>
  );
}

// Commercial Portal Sidebar
export function ComercialSidebar({ stats = {}, onLogout, isOpen, onClose }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  // Primary navigation items
  const primaryItems = [
    { to: '/comercial', icon: LayoutDashboard, label: 'Panel Comercial', exact: true },
    { to: '/comercial/mis-resultados', icon: Trophy, label: 'Mis Resultados' },
  ];

  // Action items (highlighted)
  const actionItems = [
    { to: '/comercial/nuevo-contacto', icon: UserPlus, label: 'Nuevo Contacto', highlight: true },
    { to: '/comercial/nueva-prueba', icon: Plus, label: 'Nueva Prueba', highlight: true },
  ];

  // Main navigation items
  const mainItems = [
    { to: '/comercial/crm', icon: LayoutGrid, label: 'CRM' },
    { to: '/comercial/agenda', icon: Calendar, label: 'Agenda' },
  ];

  // Secondary navigation items
  const secondaryItems = [
    { to: '/comercial/contactos', icon: Users, label: 'Contactos' },
    { to: '/comercial/pruebas', icon: Beaker, label: 'Pruebas', badge: stats.pruebas_activas || null },
    { to: '/comercial/instalaciones', icon: Wrench, label: 'Instalaciones', badge: stats.pendientes_instalacion || null },
    { to: '/comercial/objetivos', icon: Target, label: 'Objetivos' },
    { to: '/comercial/presupuestos', icon: FileText, label: 'Presupuestos' },
    { to: '/comercial/documentacion', icon: FolderOpen, label: 'Documentación' },
    { to: '/comercial/referidos', icon: Link2, label: 'Referidos' },
  ];

  // RRHH items
  const rrhhItems = [
    { to: '/comercial/vacaciones', icon: Sun, label: 'Vacaciones' },
    { to: '/comercial/fichaje', icon: Clock, label: 'Fichaje' },
  ];

  const isActive = (item) => {
    if (item.exact) return currentPath === item.to;
    return currentPath.startsWith(item.to) && item.to !== '/comercial';
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-screen bg-[#030303] border-r border-white/[0.06]
          flex flex-col z-50
          w-[280px] lg:w-72
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo + Close Button */}
        <div className="flex-shrink-0 p-4 lg:p-5 border-b border-white/[0.06] flex items-center justify-between">
          <Link to="/comercial" className="flex items-center gap-3" onClick={handleNavClick}>
            <img 
              src="/manklar-logo-white.png" 
              alt="Manklar" 
              className="h-8 lg:h-9 drop-shadow-[0_0_20px_rgba(255,255,255,0.1)]"
            />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-zinc-400 hover:text-white hover:bg-white/10"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Portal Label */}
        <div className="flex-shrink-0 px-4 lg:px-5 py-3">
          <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-600 font-medium font-['Manrope']">
            Portal Comercial
          </span>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto px-3 lg:px-4 pb-4 space-y-1">
          {/* Primary */}
          {primaryItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              active={isActive(item)}
              onClick={handleNavClick}
            />
          ))}

          {/* Actions Section */}
          <NavSection title="Acciones" />
          {actionItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              active={isActive(item)}
              onClick={handleNavClick}
            />
          ))}

          {/* Main Section */}
          <NavSection title="Gestión" />
          {mainItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              active={isActive(item)}
              onClick={handleNavClick}
            />
          ))}

          {/* Secondary Section */}
          <NavSection title="Seguimiento" />
          {secondaryItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              active={isActive(item)}
              onClick={handleNavClick}
            />
          ))}

          {/* RRHH Section */}
          <NavSection title="RRHH" />
          {rrhhItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              active={isActive(item)}
              onClick={handleNavClick}
            />
          ))}
        </nav>

        {/* Bottom Section - Fixed */}
        <div className="flex-shrink-0 p-3 lg:p-4 border-t border-white/[0.06] space-y-3 bg-[#030303]">
          {/* Portal Selector */}
          <PortalSelector variant="full" showDescription={false} />
          
          <div className="pt-2 border-t border-white/[0.06]">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
              data-testid="comercial-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-['Manrope']">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

// Global Search Component
function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/comercial/buscar`, {
          params: { q: query },
          withCredentials: true
        });
        setResults(response.data.results || []);
        setShowResults(true);
      } catch (err) {
        console.error('Search error:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (trial) => {
    setQuery('');
    setShowResults(false);
    navigate(`/comercial/cliente/${trial.id}`);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setFocused(false);
      setShowResults(false);
    }, 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { setFocused(true); if (results.length > 0) setShowResults(true); }}
          onBlur={handleBlur}
          placeholder="Buscar cliente, teléfono, empresa..."
          className="pl-9 w-48 lg:w-64 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-sky-500/50"
          data-testid="global-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />
        )}
      </div>
      
      {/* Search Results Dropdown */}
      {showResults && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#0a0a0a] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50 max-h-80 overflow-y-auto">
          {results.map((trial) => (
            <button
              key={trial.id}
              onClick={() => handleSelect(trial)}
              className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/[0.04] last:border-0"
              data-testid={`search-result-${trial.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${trial.tipo_cliente === 'empresa' ? 'bg-amber-500/20' : 'bg-sky-500/20'}`}>
                  {trial.tipo_cliente === 'empresa' ? (
                    <Building2 className="w-4 h-4 text-amber-400" />
                  ) : (
                    <Home className="w-4 h-4 text-sky-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-zinc-500">{trial.client_code}</span>
                  </div>
                  <p className="text-sm text-white truncate">
                    {trial.tipo_cliente === 'empresa' ? trial.empresa : trial.nombre_contacto}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {trial.telefono && (
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />{trial.telefono}
                      </span>
                    )}
                    {trial.ciudad && (
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{trial.ciudad}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* No results */}
      {showResults && query.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#0a0a0a] border border-white/10 rounded-xl shadow-2xl p-4 z-50">
          <p className="text-sm text-zinc-500 text-center">Sin resultados para &quot;{query}&quot;</p>
        </div>
      )}
    </div>
  );
}

// Commercial Portal Header
export function ComercialHeader({ title, subtitle, onMenuClick, children, showSearch = true }) {
  return (
    <header className="sticky top-0 z-30 bg-[#030303]/95 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="px-4 lg:px-8 py-4 flex items-center justify-between gap-4">
        {/* Left side: Menu button + Title */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden flex-shrink-0 text-zinc-400 hover:text-white hover:bg-white/10"
            onClick={onMenuClick}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-light text-white font-['Outfit'] tracking-tight truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs lg:text-sm text-zinc-500 mt-0.5 truncate font-['Manrope']">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Center: Global Search */}
        {showSearch && (
          <div className="hidden md:block">
            <GlobalSearch />
          </div>
        )}

        {/* Right side: Notifications + Custom actions */}
        <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
          <NotificationCenter />
          {children}
        </div>
      </div>
    </header>
  );
}

// Commercial Portal Layout Wrapper
export function ComercialLayout({ children, title, subtitle, stats, onLogout, headerActions }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-[#030303] overflow-x-hidden">
      <ComercialSidebar 
        stats={stats}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="lg:ml-72 min-h-screen flex flex-col">
        <ComercialHeader 
          title={title} 
          subtitle={subtitle}
          onMenuClick={() => setSidebarOpen(true)}
        >
          {headerActions}
        </ComercialHeader>
        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}

export default ComercialLayout;
